import {
  DRIVING_TRACK_INNER_RADIUS,
  DRIVING_YARD_RADIUS,
} from './stageGround';
import {
  ELEVATION_COURSE_DOWNHILL_END_X,
  ELEVATION_COURSE_ROAD_HALF_WIDTH,
  ELEVATION_COURSE_UPHILL_START_X,
} from './drivingTerrain';
import { isOnCourseRoad, type CourseRoute } from './drivingCourse';

export const DRIVING_SURFACE_TYPES = {
  ASPHALT: 'asphalt',
  GRASS: 'grass',
  GRAVEL: 'gravel',
  PUDDLE: 'puddle',
} as const;

export type DrivingSurfaceType =
  (typeof DRIVING_SURFACE_TYPES)[keyof typeof DRIVING_SURFACE_TYPES];

export interface SurfaceProfile {
  type: DrivingSurfaceType;
  name: string;
  gripMultiplier: number;
  rollingResistanceMultiplier: number;
  wetness: number;
}

export interface SurfacePatch {
  id: string;
  type: 'gravel' | 'puddle';
  x: number;
  z: number;
  radiusX: number;
  radiusZ: number;
  rotation: number;
}

export interface GlobalSurfaceCondition {
  gripMultiplier: number;
  rollingResistanceMultiplier: number;
  roadWetness?: number;
}

export interface ResolvedDrivingSurface extends SurfaceProfile {
  /** 날씨와 지역 노면을 합성한 최종 물리 계수입니다. */
  effectiveGrip: number;
  effectiveRollingResistance: number;
}

export const DRIVING_SURFACE_PATCHES: readonly SurfacePatch[] = [
  {
    id: 'infield-gravel',
    type: 'gravel',
    x: -245,
    z: 210,
    radiusX: 74,
    radiusZ: 46,
    rotation: -0.35,
  },
  {
    id: 'ring-puddle',
    type: 'puddle',
    x: 840,
    z: -840,
    radiusX: 18,
    radiusZ: 6,
    rotation: -Math.PI / 4,
  },
  {
    id: 'hill-puddle',
    type: 'puddle',
    x: 445,
    z: 0,
    radiusX: 13,
    radiusZ: 5,
    rotation: 0,
  },
];

const SURFACE_PROFILES: Record<DrivingSurfaceType, SurfaceProfile> = {
  asphalt: {
    type: DRIVING_SURFACE_TYPES.ASPHALT,
    name: '아스팔트',
    gripMultiplier: 1,
    rollingResistanceMultiplier: 1,
    wetness: 0,
  },
  grass: {
    type: DRIVING_SURFACE_TYPES.GRASS,
    name: '잔디',
    gripMultiplier: 0.58,
    rollingResistanceMultiplier: 2.7,
    wetness: 0.08,
  },
  gravel: {
    type: DRIVING_SURFACE_TYPES.GRAVEL,
    name: '자갈',
    gripMultiplier: 0.66,
    rollingResistanceMultiplier: 2.15,
    wetness: 0,
  },
  puddle: {
    type: DRIVING_SURFACE_TYPES.PUDDLE,
    name: '물웅덩이',
    gripMultiplier: 0.46,
    rollingResistanceMultiplier: 1.35,
    wetness: 1,
  },
};

function isInsidePatch(x: number, z: number, patch: SurfacePatch): boolean {
  const dx = x - patch.x;
  const dz = z - patch.z;
  const cos = Math.cos(-patch.rotation);
  const sin = Math.sin(-patch.rotation);
  const localX = dx * cos - dz * sin;
  const localZ = dx * sin + dz * cos;
  return (
    (localX * localX) / (patch.radiusX * patch.radiusX) +
      (localZ * localZ) / (patch.radiusZ * patch.radiusZ) <=
    1
  );
}

/** Three 메시와 독립적인 월드 좌표 기반 지역 노면 판정입니다. */
export function sampleDrivingSurfaceType(
  x: number,
  z: number,
  courseRoute?: CourseRoute
): DrivingSurfaceType {
  for (const patch of DRIVING_SURFACE_PATCHES) {
    if (patch.type === 'puddle' && isInsidePatch(x, z, patch)) {
      return DRIVING_SURFACE_TYPES.PUDDLE;
    }
  }
  for (const patch of DRIVING_SURFACE_PATCHES) {
    if (patch.type === 'gravel' && isInsidePatch(x, z, patch)) {
      return DRIVING_SURFACE_TYPES.GRAVEL;
    }
  }

  if (isOnCourseRoad(courseRoute, x, z)) {
    return DRIVING_SURFACE_TYPES.ASPHALT;
  }

  const radius = Math.hypot(x, z);
  if (radius >= DRIVING_TRACK_INNER_RADIUS && radius <= DRIVING_YARD_RADIUS) {
    return DRIVING_SURFACE_TYPES.ASPHALT;
  }
  if (
    x >= ELEVATION_COURSE_UPHILL_START_X - 18 &&
    x <= ELEVATION_COURSE_DOWNHILL_END_X + 18 &&
    Math.abs(z) <= ELEVATION_COURSE_ROAD_HALF_WIDTH + 1
  ) {
    return DRIVING_SURFACE_TYPES.ASPHALT;
  }
  if (radius > DRIVING_YARD_RADIUS) {
    return DRIVING_SURFACE_TYPES.GRAVEL;
  }
  return DRIVING_SURFACE_TYPES.GRASS;
}

export function resolveDrivingSurface(
  x: number,
  z: number,
  global: GlobalSurfaceCondition,
  courseRoute?: CourseRoute
): ResolvedDrivingSurface {
  const type = sampleDrivingSurfaceType(x, z, courseRoute);
  const profile = SURFACE_PROFILES[type];
  return {
    ...profile,
    wetness: Math.max(profile.wetness, global.roadWetness ?? 0),
    effectiveGrip: profile.gripMultiplier * global.gripMultiplier,
    effectiveRollingResistance:
      profile.rollingResistanceMultiplier * global.rollingResistanceMultiplier,
  };
}

export function getSurfaceName(type: DrivingSurfaceType): string {
  return SURFACE_PROFILES[type].name;
}
